import moment from 'moment';

import canShowField from '../../../Components/ResourceFieldLayout/canShowField';
import { FIELD_TYPE_CATALOG } from '../../../Components/ResourceFieldLayout/FIELD_TYPE_CATALOG';
import {
  isVigenteLayout,
  readLayoutField,
  readLayoutSections,
  readLayoutUnsectioned,
} from '../../../Components/ResourceFieldLayout/shouldRenderTaskField';

const CUSTOM_FIELDS_PREFIX = 'customFields.';

export function readColumnSettingsKey(dataIndex) {
  if (Array.isArray(dataIndex)) {
    return dataIndex.join('.');
  }

  return dataIndex;
}

export function readPermissionFieldName(column) {
  if (column.customFieldName) {
    return column.customFieldName;
  }

  const { dataIndex } = column;
  if (typeof dataIndex === 'string' && dataIndex.startsWith(CUSTOM_FIELDS_PREFIX)) {
    return dataIndex.slice(CUSTOM_FIELDS_PREFIX.length);
  }

  return dataIndex.charAt(0).toUpperCase() + dataIndex.slice(1);
}

export function flattenLayoutFields(fieldLayout) {
  const fields = readLayoutUnsectioned(fieldLayout).map(readLayoutField);
  readLayoutSections(fieldLayout).forEach((section) => {
    (section.fields ?? section.Fields ?? []).forEach((item) => {
      fields.push(readLayoutField(item));
    });
  });
  return fields;
}

function inputTypeFromFieldType(fieldType) {
  const catalogEntry = Object.values(FIELD_TYPE_CATALOG).find(
    (entry) => entry.fieldType === fieldType
  );
  return catalogEntry?.inputType ?? fieldType;
}

function readCustomFieldValue(record, fieldName) {
  const customFields = record?.customFields ?? record?.CustomFields ?? {};
  return customFields[fieldName];
}

export function renderCustomListCell(record, fieldName, fieldType, renderCurrency, renderFiles) {
  const value = readCustomFieldValue(record, fieldName);
  if (value == null || value === '') {
    return '-';
  }
  if (fieldType === 'checkbox') {
    return value ? 'Sim' : 'Não';
  }
  if (fieldType === 'currency') {
    return renderCurrency(value);
  }
  if (fieldType === 'date') {
    return moment.utc(value).isValid() ? moment.utc(value).format('DD/MM/YYYY') : '-';
  }
  if (fieldType === 'time') {
    return moment.utc(value).isValid() ? moment.utc(value).local().format('HH:mm') : '-';
  }
  if (fieldType === 'fileUpload' && Array.isArray(value)) {
    return renderFiles(value.map(({ label, url }) => ({ text: label, link: url })));
  }
  return String(value);
}

export function buildCustomListColumn(layoutField, fieldsPermissions, renderCurrency, renderFiles) {
  const fieldName = layoutField.name;
  const access = layoutField.accessHint ?? fieldsPermissions?.[fieldName]?.access ?? 0;
  const { fieldType } = layoutField;
  return {
    title: layoutField.label,
    dataIndex: `${CUSTOM_FIELDS_PREFIX}${fieldName}`,
    customFieldName: fieldName,
    isCustomField: true,
    inputType: inputTypeFromFieldType(fieldType),
    editable: access === 2,
    width: 200,
    group: 'general',
    render: (_, record) =>
      renderCustomListCell(record, fieldName, fieldType, renderCurrency, renderFiles),
  };
}

export function appendCustomFieldColumns(
  defaultColumns,
  fieldLayout,
  fieldsPermissions,
  renderCurrency,
  renderFiles
) {
  const existingNames = new Set(defaultColumns.map((column) => readPermissionFieldName(column)));
  flattenLayoutFields(fieldLayout)
    .filter((field) => field.fieldType && !existingNames.has(field.name))
    .forEach((field) => {
      defaultColumns.push(
        buildCustomListColumn(field, fieldsPermissions, renderCurrency, renderFiles)
      );
      existingNames.add(field.name);
    });
  return defaultColumns;
}

export function hasPersonalColumnSettings(columnsSettings) {
  return Array.isArray(columnsSettings) && columnsSettings.length > 0;
}

export function readSelectedColumnKeys(columnsSettings, defaultColumns, defaultVisibleColumns) {
  if (hasPersonalColumnSettings(columnsSettings)) {
    const [firstItem] = columnsSettings;
    let columns = firstItem.columns || '';
    if (!columns.split(',').includes('dealClientCNPJ')) {
      columns = `${columns},dealClientCNPJ`;
    }
    return columns.split(',');
  }

  const customKeys = defaultColumns
    .filter((column) => column.isCustomField)
    .map((column) => readColumnSettingsKey(column.dataIndex));
  return [...defaultVisibleColumns.split(','), ...customKeys];
}

export function columnHasListAccess(column, fieldsPermissions) {
  const fieldName = readPermissionFieldName(column);
  try {
    return fieldsPermissions[fieldName].access >= 1;
  } catch (error) {
    return false;
  }
}

function isTrackedByLayout(column, fieldsPermissions) {
  if (column.isCustomField) {
    return true;
  }

  const fieldName = readPermissionFieldName(column);
  return fieldsPermissions?.[fieldName] !== undefined;
}

export function canShowListColumn(column, fieldsPermissions, fieldLayout, roleId) {
  const fieldName = readPermissionFieldName(column);
  const layoutState = fieldLayout?.state ?? fieldLayout?.State ?? 'padrao';
  if (
    !canShowField({
      fieldName,
      fieldPermissions: fieldsPermissions,
      layoutState: column.isCustomField ? 'padrao' : layoutState,
      roleId,
      isNew: false,
    })
  ) {
    return false;
  }

  if (!isVigenteLayout(fieldLayout) || !isTrackedByLayout(column, fieldsPermissions)) {
    return true;
  }

  return flattenLayoutFields(fieldLayout).some((field) => field.name === fieldName);
}
